import { Injectable } from "@angular/core";
import {
  HttpHeaders,
  HttpClient,
  HttpErrorResponse,
} from "@angular/common/http";
import { ServiceOptions } from "../models/service-options";
import { Config } from "src/app/config";
import { AppState } from "../redux/store";
import { SharedService } from "./shared.service";
import {
  throwError,
  Observable,
  Subscription,
  Subject,
  merge,
  of,
  fromEvent,
} from "rxjs";
import { AwsService } from "./aws.service";
import { UiUser, AuthDetails } from "../models/ui-user.model";
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { catchError, map, take, tap } from "rxjs/operators";
import { DataElement, ServerError, ServiceCallInfo } from "../models/common";
import { ConnectionService, ConnectionState } from "ng-connection-service";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { Clinic } from "../models/clinic.model";
import { Store } from "@ngrx/store";
import { ERROR__FROM_SERVER, USER_SIGN_OUT } from "../ngrx/ngrx-actions";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  servicesUrl: string;
  getPresignedUrl: string;
  algServicesUrl: string;
  chatStatBucket: string;
  errorLogLamdaUrl: string;
  pckName: string = "brosh_func";
  refreshTokenTimer: any;
  currentUser: UiUser = new UiUser();
  isOnline = true;
  version: string = "3.0.0"; //TODO
  subscription = new Subscription();
  // Emitted just before the user is signed out, while the token still works.
  readonly signingOut$ = new Subject<void>();
  // Each carries the fresh ID token; the realtime connection follows them.
  readonly signedIn$ = new Subject<string>();
  readonly tokenRefreshed$ = new Subject<string>();
  networkStatus: boolean = false;
  networkStatus$: Subscription = Subscription.EMPTY;
  assetsUrl: string;

  constructor(
    private readonly store: Store,
    private sharedService: SharedService,
    private awsService: AwsService,
    private http: HttpClient,
    private router: Router,
    private connectionService: ConnectionService,
    private translateService: TranslateService
  ) {
    // Config-driven for every env (was hardcoded to the we-alg QA API for non-prod,
    // which defeated the config-file cutover). qa -> config.qa.json (api.qa),
    // prod -> config.prod.json. Endpoints move by flipping config, not code.
    this.servicesUrl = Config.assetConfigData["servicesUrl"];

    this.getPresignedUrl = Config.assetConfigData["getPresignedUrl"];
    this.errorLogLamdaUrl = Config.assetConfigData["errorLogLamdaUrl"];
    this.assetsUrl = Config.assetConfigData["assetsUrl"];

    this.algServicesUrl = Config.assetConfigData["algServicesUrl"];

    this.chatStatBucket = Config.assetConfigData["chatStatBucket"];

    if (environment.loggerSw) console.log(this.servicesUrl);

    this.subscription.add(
      this.connectionService
        .monitor({
          enableHeartbeat: true,
          heartbeatUrl: "/assets/config/config.json",
          heartbeatInterval: 10000,
        })
        .pipe(
          tap((state) => {
            this.isOnline =
              state.hasNetworkConnection && state.hasInternetAccess;
          })
        )
        .subscribe()
    );
  }

  getRequestUrl(serviceName: string, algServiceSw: boolean = false): string {
    if (algServiceSw) {
      return this.algServicesUrl + serviceName;
    } else return this.servicesUrl + this.pckName + "." + serviceName;
  }

  getRequestHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json; charset=UTF-8",
      Authorization: this.currentUser ? this.currentUser.idJwtToken : "",
    });
  }

  getRequestBody(options: ServiceOptions, msg?: string): string {
    if (this.currentUser) {
      if (!options.userId) options.userId = this.currentUser.userId;
      if (!options.lang) options.lang = SharedService.ui_lang;
      options.token = this.currentUser.token;
    }

    let req = new Object();
    if (options.urldecodeSw) {
      req["request"] = {};
      req["request"]["data"] = encodeURIComponent(JSON.stringify(options));
      req["request"]["urldecodeSw"] = 1;
    } else {
      req["request"] = {};
      req["request"]["serviceName"] = options.serviceName;
      req["request"]["data"] = options;
    }

    let body = JSON.stringify(req);
    let _msg: string = "";
    let _serviceName: string = options.serviceName
      ? options.serviceName
      : "request body";
    if (msg) {
      _msg = " (" + msg + ")";
    }
    if (environment.loggerSw) {
      console.groupCollapsed(
        "%c" + _serviceName + " | " + _msg,
        "color: LightSlateGray"
      );
      console.log(this.servicesUrl);
      console.log(body);
      console.table(options);
      console.groupEnd();
    }

    return body;
  }

  extractRequestStatusNew(info: ServiceCallInfo, rep: any): boolean {
    if (environment.loggerSw) {
      console.groupCollapsed(
        "%c" + info.serviceName + " | REPLY",
        "color: LightSlateGray"
      );
      console.log(this.servicesUrl);
      console.log(rep);
      console.groupEnd();
    }

    let errorSw: boolean;
    let statusCode: string;
    let statusMsg: string;

    if (info.algServicesSw) {
      if (rep.reqStatus.statusCode !== 0) {
        errorSw = true;
        statusCode = rep.reqStatus.status;
        if (rep.req != null) {
          statusMsg = rep.req.statusMessage;
        }
      }
    } else if (rep.reply.requestStatus.statusCode !== 0) {
      if (rep.reply.requestStatus.status == "AUTH_ERR") {
        this.signOutUser().then(() => {
          this.store.dispatch(USER_SIGN_OUT());
          this.router.navigate([""]);
        });
      } else if (rep.reply.requestStatus.status == "MAINTENANCE") {
        errorSw = true;
        statusCode = rep.reply.requestStatus.statusCode;
        statusMsg = rep.reply.requestStatus.statusMessage;
        this.store.dispatch(USER_SIGN_OUT());
        this.router.navigate([""]);
      } else {
        errorSw = true;
        statusCode = rep.reply.requestStatus.statusCode;
        statusMsg = rep.reply.requestStatus.statusMessage;
      }
    }

    if (errorSw) {
      let serverError = new ServerError();
      serverError.message = statusMsg;
      serverError.errorCode = statusCode;
      serverError.url = info.url;
      serverError.serviceName = info.serviceName;
      serverError.req = info.req;
      serverError.rep = JSON.stringify(rep);
      throw serverError;
    }

    return true;
  }

  fromXMLBoolean(inputBool: string): boolean {
    if (!inputBool) return false;
    return String(inputBool).toUpperCase() === "1";
  }

  fromXMLDate(inputDate: string): Date {
    if (inputDate === "" || !inputDate) return undefined;
    return new Date(inputDate.replace(" ", "T"));
  }

  handleErrorNew(error: any, info: ServiceCallInfo) {
    let errorData = {
      serviceName: info.serviceName,
      error: error.message ? error.message : error,
    };
    this.store.dispatch(
      ERROR__FROM_SERVER({
        data: errorData,
      })
    );

    let offlineCall = !this.isOnline || !info.online;

    if (!info.alertOnError) {
      // a background call (e.g. a push nudge's refresh): logged below, no dialog
    } else if (!offlineCall) {
      this.sharedService.alertError(
        error.message,
        null,
        null,
        info.serviceName
      );
    } else if (info.alertWhenOffline) {
      this.sharedService.alertError(
        null,
        this.translateService.instant("translate.alert.networkErrorTitle"),
        this.translateService.instant("translate.alert.networkError"),
        info.serviceName
      );
    }

    let serverError = new ServerError();
    if (error.isServerError) {
      serverError = error;
    } else {
      serverError.message = error.message || error.toString();
      serverError.errorCode = error.name;
      serverError.url = info.url;
      serverError.serviceName = info.serviceName;
      serverError.req = info.req;
    }

    this.processError(serverError);

    return throwError(error);
  }

  processError(e: any) {
    let encodedBody = JSON.stringify({
      serviceName: e.serviceName ?? "",
      url: e.url ?? "",
      errorCode: e.errorCode ?? "",
      message: e.message ?? "",
      networkError: e.networkError ?? "",
      req: e.req ?? "",
      rep: e.rep ?? "",
      userId: this.currentUser?.username ?? "",
      timestamp: new Date(),
      project: "brosh",
    });

    let body = {
      data: encodeURI(encodedBody),
    };
    try {
      this.http
        .post(this.errorLogLamdaUrl, JSON.stringify(body), {
          headers: this.getRequestHeaders(),
          withCredentials: false,
          responseType: "json",
        })
        .pipe(map((data: any) => console.log(data)))
        .subscribe();
    } catch (e) {
      console.log(e);
    }
  }

  loginUser(username: string, password: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.awsService.authenticateUserPool(
        username,
        password,
        (message: any, result: any, user: any) => {
          if (message === "EMPTY_TOKEN") {
            resolve({ value: "EMPTY_TOKEN", error: undefined });
          } else {
            if (message != null) {
              if (message.code == "InvalidParameterException") {
                reject({
                  value: "ERROR",
                  error: this.translateService.instant(
                    "translate.login.invalidUsernameMsg"
                  ),
                });
              } else reject({ value: "ERROR", error: message.message });
            } else {
              this.setCurrentUser(false, result, user);
              resolve({ value: "OK", error: undefined });
            }
          }
        }
      );
    });
  }

  forgotPassword(username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.awsService.forgotPassword(
        username,
        (message: any, result: any, user: any) => {
          if (message === "SUCCESS") {
            resolve({ value: "OK", error: undefined });
          } else {
            reject({ value: "ERROR", error: result.message });
          }
        }
      );
    });
  }

  setCurrentUser(
    rertievedSessionSw: boolean,
    cognitoAuthResult: any,
    cognitoUser: any
  ) {
    if (rertievedSessionSw) {
      this.currentUser.accessJwtToken =
        cognitoUser.signInUserSession.accessToken.jwtToken;
      this.currentUser.idJwtToken =
        cognitoUser.signInUserSession.idToken.jwtToken;
      this.currentUser.refreshToken =
        cognitoUser.signInUserSession.refreshToken.token;
      this.currentUser.refreshTokenObject =
        cognitoUser.signInUserSession.refreshToken;
    } else {
      this.currentUser.accessJwtToken = cognitoAuthResult.accessToken.jwtToken;
      this.currentUser.idJwtToken = cognitoAuthResult.idToken.jwtToken;
      this.currentUser.refreshToken = cognitoAuthResult.refreshToken.token;
      this.currentUser.refreshTokenObject = cognitoAuthResult.refreshToken;
    }

    this.currentUser.username = cognitoUser.username;
    this.currentUser.isLoggedIn = true;
    this.currentUser.cognitoUserObject = cognitoUser;

    cognitoUser.getUserAttributes((err, result) => {
      if (!err) {
        this.currentUser.phone = result.find((i) => {
          return i["Name"] === "phone_number";
        })["Value"];
      }
    });

    if (this.refreshTokenTimer) clearInterval(this.refreshTokenTimer);
    this.refreshTokenTimer = setInterval(() => {
      this.refreshUser();
    }, 1000 * 60 * 20);
    // A restored session is refreshed straight away and announced with the fresh
    // token by retrieveUserSession, so the live connection opens once, not twice.
    if (!rertievedSessionSw) this.signedIn$.next(this.currentUser.idJwtToken);
  }

  forgetUser() {
    if (this.currentUser) {
      this.currentUser.isLoggedIn = false;
      if (this.refreshTokenTimer) clearInterval(this.refreshTokenTimer);
    }

    this.currentUser = new UiUser();
  }

  refreshUser(): Promise<any> {
    const user = this.currentUser;
    return new Promise((resolve, reject) => {
      this.awsService.refreshTokens(
        user.cognitoUserObject,
        user.refreshTokenObject,
        (err, session) => {
          if (err) {
            // this.forgetUser();
            reject();
          } else {
            user.accessJwtToken = session.accessToken.jwtToken;
            user.idJwtToken = session.idToken.jwtToken;
            // A refresh that lands after sign-out (forgetUser replaced the user)
            // must not reconnect the live channel.
            if (user === this.currentUser && user.isLoggedIn) this.tokenRefreshed$.next(user.idJwtToken);
            resolve(true);
          }
        }
      );
    });
  }

  signOutUser(): Promise<any> {
    this.signingOut$.next();
    return new Promise((resolve, reject) => {
      this.awsService.signOutUser(
        this.currentUser.cognitoUserObject,
        (result: any) => {
          console.log(result);
          this.forgetUser();
          resolve(true);
        }
      );
    });
  }

  retrieveUserSession(username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.awsService.retrieveUserSession(
        (sessionValid: boolean, cognitoUser: any) => {
          if (sessionValid && cognitoUser.username == username.toUpperCase()) {
            if (cognitoUser) {
              this.setCurrentUser(true, null, cognitoUser);
              this.refreshUser().then(
                (success) => {
                  this.signedIn$.next(this.currentUser.idJwtToken);
                  resolve(true);
                },
                (error) => reject()
              );
            } else {
              reject();
            }
          } else {
            reject();
          }
        }
      );
    });
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    let user = this.currentUser;

    if (user.isLoggedIn) {
      switch (state.url) {
        case "/" + this.sharedService.routeEnum.REPORTS:
          return this.currentUser.authDetails.getBooleanValue(
            "INVOICES.VIEW_INVOICES_SW"
          );

        case "/" + this.sharedService.routeEnum.USERS:
          return this.currentUser.authDetails.getBooleanValue(
            "USERS_SCR.ALLOWED_SW"
          );

        case "/" + this.sharedService.routeEnum.SUPPLIES:
          return (
            this.currentUser.authDetails.getBooleanValue(
              "DELIVERY.REQUEST_DELIVERY_SW"
            ) ||
            this.currentUser.authDetails.getBooleanValue(
              "DELIVERY.APPROVE_DELIVERY_SW"
            )
          );

        default:
          return true;
      }
    } else return false;
  }

  getUserDetails(): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "get_user_details";
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params["username"] = this.currentUser.username;

    let url = this.getRequestUrl(serviceName);
    let body = this.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => {
          return this.extractUserDetails(data, info);
        }),
        catchError((error) => {
          return this.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractUserDetails(res: any, info: ServiceCallInfo): boolean {
    this.extractRequestStatusNew(info, res);

    let data = res.reply.requestResult.data;
    if (!data) return false;

    if (data) {
      this.currentUser.userDescr = data.fullName;
      this.currentUser.userId = data.userId;
      this.currentUser.lang = data.lang;
      this.currentUser.roleCode = data.roleCode;
      this.currentUser.roleDescr = data.roleDescr;
      this.currentUser.agentCode = data.agentCode;
      this.currentUser.org = data.org;
      this.currentUser.allowCreateOrderSw = data.allowCreateOrderSw;
      this.currentUser.authDetails = new AuthDetails();

      this.currentUser.permittedClinicList = [];

      if (data.clinicList && data.clinicList.row) {
        let arr = new Array().concat(data.clinicList.row);
        arr.forEach((i) => {
          this.currentUser.permittedClinicList.push(
            new DataElement(i.clinicId, i.clinicDescr)
          );
        });
      }

      if (data.currentClinic)
        this.currentUser.clinic = new Clinic(
          data.currentClinic,
          this.currentUser.permittedClinicList.find(i => i.id == data.currentClinic)?.text
        );

      if (data.plantId) {
        this.currentUser.plant = new DataElement(data.plantId, data.plantDescr);
      }

      if (data.roleSettings && data.roleSettings.setting) {
        let arr = new Array().concat(data.roleSettings.setting);
        arr.forEach((i) => {
          this.currentUser.authDetails.setValue(i.key, i.value);
        });
      }


    }

    return true;
  }

  appSignIn(password: string, clinicId: string = null): Observable<void> {
    let response = new Observable<void>();
    let serviceName = "sign_in";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["username"] = this.currentUser.username.toUpperCase();
    params["password"] = password;
    if (clinicId) {
      params["clinicId"] = clinicId;
    }

    let url = this.getRequestUrl(serviceName);
    let body = this.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractAppSignIn(data, password, info)),
        catchError((error) => {
          return this.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractAppSignIn(res: any, password: string, info: ServiceCallInfo) {
    this.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;

    if (data) {
      this.currentUser.userId = data.userId;
      this.currentUser.token = data.token;
      this.currentUser.password = password;
    }
  }

  updateUserPassword(password: string): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "update_user_password";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["username"] = this.currentUser.username.toUpperCase();
    params["newPassword"] = password;
    params["phone"] = this.currentUser.phone;

    let url = this.getRequestUrl(serviceName);
    let body = this.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.handleErrorNew(error, info);
        })
      );

    return response;
  }
}
